// /src/services/clubService.js
import { db } from '@/firebase/firebase';
import {
  collection, doc, getDocs, setDoc, query, where, orderBy,
  startAt, endAt, limit as fbLimit, serverTimestamp, documentId,
  writeBatch, arrayUnion, or, getDoc,
} from 'firebase/firestore';
import { makeSearchTokens, normalizeName } from '@/utils/name';

const CATALOG = 'teams_catalog';
const CLUBS   = 'clubs';

// Catalog may use any of these token field names.
const TOKEN_FIELDS = ['searchTokens', 'tokens'];

// We'll try prefix search on these fields in order.
const PREFIX_FIELDS = ['name', 'slug'];

/* ----------------------------- SEARCH ----------------------------- */
/**
 * Search clubs across the seeded catalog and user-created clubs.
 * Tries token search (searchTokens/tokens) and several prefix fallbacks
 * (name, slug, documentId).
 */
export async function searchClubs(term, lim = 12) {
  const qTerm = (term || '').trim();
  if (!qTerm) return [];

  const prefix = normalizeName(qTerm);            // lowercased, stripped
  const tokens = makeSearchTokens(qTerm).slice(0, 10);

  const out = [];
  const seen = new Set();

  const push = (source, id, data) => {
    const key = `${source}:${id}`;
    if (seen.has(key)) return;
    seen.add(key);
    const nm = data?.name || data?.displayName || '';
    out.push({
      id,
      name: nm,
      aliases: Array.isArray(data?.aliases) ? data.aliases : [],
      slug: data?.slug || null,
      _score: score(prefix, tokens, nm),
    });
  };

  // --- 1) Catalog token search (primary path with your schema) ---
  const catalog = collection(db, CATALOG);
  for (const field of TOKEN_FIELDS) {
    try {
      if (!tokens.length) break;
      const qRef = query(catalog, where(field, 'array-contains-any', tokens), fbLimit(lim));
      const s = await getDocs(qRef);
      s.forEach(d => push('cat', d.id, d.data() || {}));
      if (!s.empty) break; // good enough
    } catch (e) {
      // try next token field name
      // console.debug('[clubService] token query failed on', field, e?.code || e);
    }
  }

  // --- 2) Catalog prefix fallbacks (name/slug) ---
  for (const field of PREFIX_FIELDS) {
    try {
      const qRef = query(
        catalog,
        orderBy(field),
        startAt(qTerm),
        endAt(qTerm + '\uf8ff'),
        fbLimit(lim)
      );
      const s = await getDocs(qRef);
      s.forEach(d => push('cat', d.id, d.data() || {}));
    } catch (e) {
      // console.debug('[clubService] prefix failed on', field, e?.code || e);
    }
  }

  // --- 3) Catalog documentId prefix (helps if IDs are slugs like "legends-fc") ---
  try {
    const qRef = query(
      catalog,
      orderBy(documentId()),
      startAt(prefix),
      endAt(prefix + '\uf8ff'),
      fbLimit(lim)
    );
    const s = await getDocs(qRef);
    s.forEach(d => push('cat', d.id, d.data() || {}));
  } catch (e) {
    // ignore
  }

  // --- 4) User clubs (prefix + tokens) ---
  const clubs = collection(db, CLUBS);
  // prefix on name
  try {
    const qRef = query(
      clubs,
      orderBy('name'),
      startAt(qTerm),
      endAt(qTerm + '\uf8ff'),
      fbLimit(lim)
    );
    const s = await getDocs(qRef);
    s.forEach(d => push('usr', d.id, d.data() || {}));
  } catch {}

  // tokens (we store both searchTokens & tokens for user clubs we create)
  for (const field of TOKEN_FIELDS) {
    try {
      if (!tokens.length) break;
      const qRef = query(clubs, where(field, 'array-contains-any', tokens), fbLimit(lim));
      const s = await getDocs(qRef);
      s.forEach(d => push('usr', d.id, d.data() || {}));
      if (!s.empty) break;
    } catch {}
  }

  // rank & return
  out.sort((a, b) => b._score - a._score || a.name.localeCompare(b.name));
  return out.slice(0, lim).map(({ _score, ...rest }) => rest);
}

// Simple ranking: strong boost for hard prefix, then token matches.
function score(prefix, tokens, name) {
  const lower = (name || '').toLowerCase();
  let s = lower.startsWith(prefix) ? 100 : 0;
  for (const t of tokens) if (lower.includes(t)) s += 5;
  return s;
}

/* -------------------------- CREATE / GET -------------------------- */
/**
 * Create a user club only if there is no existing catalog/user club
 * whose normalized name matches exactly (case-insensitive).
 */
export async function createOrGetClubByName(name, userId) {
  const clean = (name || '').trim();
  if (!clean) throw new Error('Club name required');

  const normalized = normalizeName(clean);
  const toks = makeSearchTokens(clean);

  // 1) Try to find exact normalized match in catalog via token query then compare.
  try {
    for (const field of TOKEN_FIELDS) {
      const qRef = query(collection(db, CATALOG), where(field, 'array-contains-any', toks), fbLimit(20));
      const s = await getDocs(qRef);
      const exact = s.docs.find(d => normalizeName(d.data()?.name || '') === normalized);
      if (exact) {
        const data = exact.data() || {};
        return { id: exact.id, name: data.name || clean, slug: data.slug || null };
      }
    }
  } catch {}

  // 2) Exact case-sensitive match on catalog.name (helps if user typed exact case)
  try {
    const s = await getDocs(
      query(collection(db, CATALOG), where('name', '==', clean), fbLimit(1))
    );
    if (!s.empty) {
      const d = s.docs[0], data = d.data() || {};
      return { id: d.id, name: data.name || clean, slug: data.slug || null };
    }
  } catch {}

  // 3) Existing user club with same normalized name
  try {
    // We saved both searchTokens/tokens for user clubs too; use that then compare.
    for (const field of TOKEN_FIELDS) {
      const qRef = query(collection(db, CLUBS), where(field, 'array-contains-any', toks), fbLimit(20));
      const s = await getDocs(qRef);
      const exact = s.docs.find(d => normalizeName(d.data()?.name || '') === normalized);
      if (exact) {
        const data = exact.data() || {};
        return { id: exact.id, name: data.name || clean, slug: data.slug || null };
      }
    }
  } catch {}

  // 4) Create new user club
  const ref = doc(collection(db, CLUBS));
  await setDoc(ref, {
    name: clean,
    // Store for future-proof searches:
    searchTokens: toks,
    tokens: toks,
    // Optional aliases bucket
    aliases: [],
    createdBy: userId || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: ref.id, name: clean };
}

/* ----------------------- TEAM ATTACHMENT ----------------------- */
/**
 * Find teams that the user is owner/coach/staff of and that aren't yet
 * tied to any club (or are already in this specific club). Used by
 * CreateClub (post-create offer) and ClubSettings (manual attach UI).
 *
 * Query is intentionally over-broad on the client (returns teams the
 * user manages) and then filtered for clubId-null OR clubId == thisClub.
 * That way we surface "already attached to a different club" teams as
 * disabled options rather than just hiding them — clearer UX.
 *
 * Returns: Array<{ id, name, sport, ageGroup, gender, logoUrl, clubId, alreadyHere, alreadyElsewhere }>
 */
export async function findAttachableTeamsForUser({ uid, clubId }) {
  if (!uid) return [];

  const teamsRef = collection(db, 'teams');

  // Run ownerId / coachId / staffIds queries in parallel. We can't use
  // a single OR query because Firestore's documentId() OR composite has
  // index limits — three small queries are simpler.
  const queries = [
    query(teamsRef, where('ownerId',  '==', uid)),
    query(teamsRef, where('coachId',  '==', uid)),
    query(teamsRef, where('staffIds', 'array-contains', uid)),
  ];

  const seen = new Map(); // teamId -> raw doc data
  const snaps = await Promise.all(queries.map((q) => getDocs(q).catch(() => null)));
  for (const snap of snaps) {
    if (!snap) continue;
    snap.forEach((d) => {
      if (!seen.has(d.id)) seen.set(d.id, d.data() || {});
    });
  }

  return Array.from(seen.entries()).map(([id, data]) => {
    const teamClubId = data.clubId || null;
    return {
      id,
      name: data.name || data.teamName || 'Team',
      sport: data.sport || null,
      ageGroup: data.ageGroup || null,
      gender: data.gender || null,
      logoUrl: data.logoUrl || null,
      clubId: teamClubId,
      clubName: data.clubName || null,
      alreadyHere: !!(clubId && teamClubId === clubId),
      alreadyElsewhere: !!(teamClubId && (!clubId || teamClubId !== clubId)),
    };
  }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/**
 * Attach one or more existing teams to a club. Writes BOTH sides of the
 * link atomically:
 *   • teams/{teamId}.clubId / clubName / clubSlug
 *   • clubs/{clubId}.teamIds (arrayUnion)
 *
 * Mirrors the forward-create path in CreateTeams.jsx so that ClubBroadcast
 * (and any other surface that reads club.teamIds) sees the same shape
 * regardless of whether the team was created before or after the club.
 *
 * Skips any teamId already pointing at a DIFFERENT club to prevent
 * accidental club hijacking — caller should pre-filter by alreadyElsewhere
 * and prompt the user before overriding.
 *
 * @returns {Promise<{ attached: string[], skipped: string[] }>}
 */
export async function attachTeamsToClub({ clubId, teamIds }) {
  if (!clubId) throw new Error('clubId required');
  const ids = Array.isArray(teamIds) ? teamIds.filter(Boolean) : [];
  if (!ids.length) return { attached: [], skipped: [] };

  // Fetch the club doc once for clubName / clubSlug to denormalize
  // onto the team docs (matches the CreateTeams.jsx forward path).
  const clubSnap = await getDoc(doc(db, 'clubs', clubId));
  if (!clubSnap.exists()) throw new Error('Club not found');
  const club = clubSnap.data() || {};

  // Pre-flight: drop any team already pointing at a different club so
  // we don't silently steal it. Caller should warn-then-override
  // explicitly via a separate path if needed.
  const attached = [];
  const skipped = [];
  const teamWrites = [];
  for (const teamId of ids) {
    const tSnap = await getDoc(doc(db, 'teams', teamId));
    if (!tSnap.exists()) { skipped.push(teamId); continue; }
    const t = tSnap.data() || {};
    if (t.clubId && t.clubId !== clubId) { skipped.push(teamId); continue; }
    attached.push(teamId);
    teamWrites.push({ teamId });
  }

  if (!attached.length) return { attached: [], skipped };

  // Single batched write so partial failures don't leave one side dangling.
  const batch = writeBatch(db);
  for (const { teamId } of teamWrites) {
    batch.set(
      doc(db, 'teams', teamId),
      {
        clubId,
        clubName: club.name || null,
        clubSlug: club.slug || null,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }
  batch.set(
    doc(db, 'clubs', clubId),
    {
      teamIds: arrayUnion(...attached),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  await batch.commit();

  return { attached, skipped };
}
